import Box from "@mui/material/Box";
import type { SxProps, Theme } from "@mui/material/styles";
import Typography from "@mui/material/Typography";
import { type RefObject, useEffect, useRef } from "react";

import { BODY, HEADING_2 } from "@/components/marketing/marketingType";
import {
  MID,
  NOT_WIDE,
  TINY,
  marketingPalette,
} from "@/components/marketing/tokens";
import type { Laid } from "@/content/landing/sectionLayout";
import {
  LandingButton,
  LandingImage,
  LandingSection,
} from "../LandingPrimitives";
import { Lines } from "@/components/marketing/Lines";

type Split = Laid<"split">;

/**
 * `.chrome_box.is-white` and `.chrome_box.is-black`: the two boxed sections of
 * the extension page. The white card sets its picture beside the copy; the
 * dark card numbers its lines on a translucent well, closes on two buttons,
 * and carries its art behind the copy, past its own edge, sliding with the
 * scroll. Neither section pads: each card is the whole of its section, and
 * the cards sit flush against the hero above and each other.
 */

/** `.chrome_box`: the rule and radius the page's boxes share. */
const BOX = {
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
  width: 1,
  p: "32px",
  borderRadius: "32px",
  border: `1px solid ${marketingPalette.outline}`,
  [NOT_WIDE]: { flexDirection: "column" },
  [TINY]: { borderRadius: "24px" },
} satisfies SxProps<Theme>;

/**
 * `.is-white`: a deeper foot, and the copy over the picture once stacked. Its
 * padding runs the whole ladder itself, so the pair of cards do not trade steps.
 */
const WHITE = {
  gap: "96px",
  pb: "96px",
  [NOT_WIDE]: { flexDirection: "column-reverse", gap: "16px", pb: "64px" },
  [MID]: { p: "48px 0" },
} satisfies SxProps<Theme>;

/**
 * `.chrome-section_img`: half the row, a little taller than square, cropped
 * to fit. It keeps its half at the middle step, which is why the copy beside
 * it runs a few pixels past the card's padding there, as published.
 */
const WHITE_ART = {
  width: "50%",
  aspectRatio: "2 / 2.15",
  objectFit: "cover",
  [NOT_WIDE]: { width: "70%" },
  [MID]: { width: 1 },
} satisfies SxProps<Theme>;

/**
 * `.header-left_chrome-extension`: the copy column, which fills what the
 * picture leaves and will not go under 400 until the card step.
 */
const COPY = {
  display: "flex",
  flexDirection: "column",
  alignItems: "flex-start",
  gap: "12px",
  width: 1,
  minWidth: "400px",
  [NOT_WIDE]: { alignItems: "center" },
  [MID]: { minWidth: "auto" },
  [TINY]: { alignItems: "stretch" },
} satisfies SxProps<Theme>;

/** The published dark ground, a diamond gradient the card is set on. */
const BLACK_GROUND =
  "/marketing/webflow/664f37c953faeb1be9d2a3a5_background.svg";

/**
 * `.is-black`: a card of fixed height whose copy keeps to the right, clipping
 * the art pinned past its edge; stacked, it grows to its content and the art
 * comes into the flow under the copy.
 */
const BLACK = {
  gap: "100px",
  justifyContent: "flex-end",
  height: "660px",
  position: "relative",
  overflow: "hidden",
  backgroundImage: `url(${BLACK_GROUND})`,
  backgroundPosition: "50%",
  backgroundRepeat: "no-repeat",
  backgroundSize: "cover",
  [NOT_WIDE]: {
    flexDirection: "column-reverse",
    gap: "32px",
    height: "auto",
    p: "64px",
  },
  [MID]: { p: "32px" },
  [TINY]: { p: "24px" },
} satisfies SxProps<Theme>;

/**
 * `.chrome-black-box_img`: as wide as the card, set 120 below and 541 left of
 * it, so the card shows its upper right. The transform is the interaction's,
 * set inline so the server sends the resting position.
 */
const BLACK_ART = {
  display: "block",
  maxWidth: "100%",
  position: "absolute",
  bottom: "-120px",
  left: "-541px",
  width: 1,
  aspectRatio: "16 / 9.7",
  objectFit: "contain",
  willChange: "transform",
  [NOT_WIDE]: { position: "static", width: "auto" },
} satisfies SxProps<Theme>;

/** `.header-left_chrome-extension.is-black`: the same column, opened to 24 and capped. */
const BLACK_COPY = {
  display: "flex",
  flexDirection: "column",
  alignItems: "flex-start",
  gap: "24px",
  width: 1,
  minWidth: "400px",
  maxWidth: "419.2px",
  [NOT_WIDE]: { alignItems: "center", maxWidth: "none" },
  [MID]: { minWidth: "auto" },
  [TINY]: { alignItems: "stretch" },
} satisfies SxProps<Theme>;

/** `.extension-steps_wrap`: the numbered lines on a translucent well. */
const STEPS = {
  display: "flex",
  flexDirection: "column",
  gap: "24px",
  width: 1,
  p: "24px",
  borderRadius: "16px",
  backgroundColor: "rgba(255, 255, 255, 0.09)",
  [TINY]: { gap: "16px", p: "16px" },
} satisfies SxProps<Theme>;

/** `.extension-steps_item` */
const STEP = {
  display: "flex",
  alignItems: "center",
  gap: "16px",
  color: "common.white",
  fontSize: 16,
  lineHeight: "24px",
  [TINY]: { fontSize: 15, lineHeight: "22.5px" },
} satisfies SxProps<Theme>;

/** `.extension-steps_number`: the numeral on its pale disc. */
const NUMERAL = {
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  width: "30.08px",
  height: "30.08px",
  borderRadius: "100vw",
  backgroundColor: "#F2F4FF",
  color: "#1D2A28",
  fontSize: 15.04,
  lineHeight: "22.56px",
  fontWeight: 500,
  [TINY]: { flex: "none", width: "24px", height: "24px" },
} satisfies SxProps<Theme>;

/**
 * `.chrome-buttons_wrap`: the filled control takes whatever room the hugging
 * one leaves, until the tiny step stacks them and opens both to the column.
 */
const BUTTONS = {
  display: "flex",
  alignItems: "center",
  justifyContent: "flex-start",
  gap: "8px",
  width: 1,
  "& > :first-child": { width: 1 },
  "& > :first-child ~ *": { flex: "none", width: "auto" },
  [NOT_WIDE]: { justifyContent: "center" },
  [TINY]: {
    flexDirection: "column",
    alignItems: "stretch",
    "& > :first-child ~ *": { width: 1 },
  },
} satisfies SxProps<Theme>;

/**
 * The art's "while scrolling in view" interaction, as published: it rests a
 * fifth of its height down until the card is a quarter of the way through the
 * viewport, rises while the card crosses the next 58%, and rests at 0 from 83%
 * on. Progress is the interaction's own: 0 as the card's top reaches the
 * bottom of the viewport, 1 as its bottom leaves the top.
 */
const REST_SHIFT = 20;
const RISE_FROM = 0.25;
const RISE_TO = 0.83;

const shift = (progress: number) => {
  const risen = Math.min(
    1,
    Math.max(0, (progress - RISE_FROM) / (RISE_TO - RISE_FROM))
  );
  return `translate3d(0px, ${(REST_SHIFT * (1 - risen)).toFixed(4)}%, 0px)`;
};

function useScrollParallax(
  card: RefObject<HTMLDivElement | null>,
  art: RefObject<HTMLImageElement | null>
) {
  useEffect(() => {
    const element = card.current;
    const image = art.current;
    if (!element || !image) {
      return;
    }
    let queued = 0;
    const place = () => {
      queued = 0;
      const rect = element.getBoundingClientRect();
      image.style.transform = shift(
        (window.innerHeight - rect.top) / (window.innerHeight + rect.height)
      );
    };
    const onScroll = () => {
      queued ||= requestAnimationFrame(place);
    };
    place();
    window.addEventListener("scroll", onScroll, { passive: true });
    window.addEventListener("resize", onScroll);
    return () => {
      window.removeEventListener("scroll", onScroll);
      window.removeEventListener("resize", onScroll);
      if (queued) {
        cancelAnimationFrame(queued);
      }
    };
  }, [card, art]);
}

/** One of the extension page's boxed sections, by the card the layout names. */
export function ChromeBox({ block }: { block: Split }) {
  return (
    <LandingSection padding="none">
      {block.chromeBox === "black" ? (
        <BlackBox block={block} />
      ) : (
        <WhiteBox block={block} />
      )}
    </LandingSection>
  );
}

function WhiteBox({ block }: { block: Split }) {
  return (
    <Box sx={[BOX, WHITE]}>
      {block.image ? <LandingImage src={block.image} sx={WHITE_ART} /> : null}
      <Box sx={COPY}>
        <Typography component="h2" sx={HEADING_2}>
          <Lines text={block.title} />
        </Typography>
        {block.paragraphs?.map((paragraph, index) => (
          <Typography key={index} sx={BODY}>
            {paragraph}
          </Typography>
        ))}
      </Box>
    </Box>
  );
}

function BlackBox({ block }: { block: Split }) {
  const card = useRef<HTMLDivElement>(null);
  const art = useRef<HTMLImageElement>(null);
  useScrollParallax(card, art);
  return (
    <Box ref={card} sx={[BOX, BLACK]}>
      {block.image ? (
        <Box
          component="img"
          ref={art}
          src={block.image}
          alt=""
          loading="lazy"
          style={{ transform: shift(0) }}
          sx={BLACK_ART}
        />
      ) : null}
      <Box sx={BLACK_COPY}>
        <Typography component="h2" sx={[HEADING_2, { color: "common.white" }]}>
          <Lines text={block.title} />
        </Typography>
        {block.bullets?.length ? (
          <Box sx={STEPS}>
            {block.bullets.map((step, index) => (
              <Box key={index} sx={STEP}>
                <Box sx={NUMERAL}>{index + 1}</Box>
                <Box>{step}</Box>
              </Box>
            ))}
          </Box>
        ) : null}
        {block.ctas?.length ? (
          <Box sx={BUTTONS}>
            {block.ctas.map((cta, index) => (
              <LandingButton
                key={index}
                cta={cta}
                variant={index === 0 ? "primary" : "onDark"}
              />
            ))}
          </Box>
        ) : null}
      </Box>
    </Box>
  );
}
